class BBBehindTheChair {
  constructor(root) {
    this.root = root;
    this.sectionId = root.dataset.sectionId;
    this.isStandalone = root.dataset.behindChairStandalone === 'true';
    this.scroller = root.querySelector('[data-behind-chair-scroller]');
    this.allCards = [...root.querySelectorAll('[data-behind-chair-card]')];
    this.cards = [];
    this.progressRoot = root.querySelector('[data-behind-chair-progress]');
    this.progressTrack = root.querySelector('[data-behind-chair-progress-track]');
    this.carouselRoot = root.querySelector('.bb-forge-masters__behind-chair-carousel');
    this.progressSegments = [];
    this.playlist = [];
    this.currentIndex = 0;
    this.isMuted = true;
    this.playbackToken = 0;
    this.playingVideo = null;
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.onEnded = this.handleEnded.bind(this);
    this.onMasterSelect = (event) => {
      if (event.detail.sectionId !== this.sectionId) return;
      this.applyMasterFilter(event.detail.blockId, { autoplay: !this.prefersReducedMotion });
    };

    this.allCards.forEach((card) => {
      const video = card.querySelector('.bb-forge-masters__behind-chair-video');
      if (!video) return;
      video.setAttribute('playsinline', '');
      video.muted = true;
    });

    this.bindEvents();

    if (this.isStandalone) {
      this.initStandalone();
      return;
    }

    document.addEventListener('forge-master:select', this.onMasterSelect);

    const initialBlockId = root.dataset.activeBlockId || this.allCards[0]?.dataset.blockId || '';
    this.applyMasterFilter(initialBlockId, { autoplay: !this.prefersReducedMotion });
  }

  bindEvents() {
    this.allCards.forEach((card) => {
      const selectButton = card.querySelector('[data-behind-chair-select]');
      if (!selectButton) return;

      selectButton.addEventListener('click', (event) => {
        if (event.target.closest('[data-behind-chair-mute]')) return;

        const index = this.playlist.findIndex((item) => item.card === card);
        if (index === -1) return;

        const video = card.querySelector('.bb-forge-masters__behind-chair-video');
        const isActiveCard = card.classList.contains('is-active');
        const isPlaying = video && !video.paused && !video.ended;

        if (isActiveCard && index === this.currentIndex) {
          if (video?.ended) {
            this.playCurrent();
            return;
          }

          if (isPlaying) {
            this.pauseCurrent();
          } else {
            this.resumeCurrent();
          }
          return;
        }

        if (this.playingVideo) {
          this.playbackToken += 1;
          this.detachVideoListeners(this.playingVideo);
          this.playingVideo.pause();
          this.playingVideo.classList.remove('is-playing');
          this.playingVideo = null;
          this.syncVisiblePlaybackUI();
        }

        this.currentIndex = index;
        this.playCurrent();
      });
    });

    this.root.querySelectorAll('[data-behind-chair-mute]').forEach((control) => {
      control.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.toggleMute();
      });

      control.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        event.stopPropagation();
        this.toggleMute();
      });
    });

    this.root.querySelectorAll('.bb-forge-masters__behind-chair-social').forEach((link) => {
      link.addEventListener('click', (event) => {
        event.stopPropagation();
      });
    });
  }

  initStandalone() {
    this.cards = this.allCards;
    this.playlist = this.buildPlaylist();
    this.rebuildProgressSegments();

    if (this.carouselRoot) {
      this.carouselRoot.hidden = this.cards.length === 0;
    }

    this.root.classList.toggle('bb-forge-masters__behind-chair--single-slide', this.cards.length === 1);

    if (this.playlist.length === 0) return;

    this.currentIndex = 0;
    this.finalizeActiveSlide(this.playlist[0].card, false);

    if (!this.prefersReducedMotion) {
      this.playCurrent();
    } else {
      this.updateActiveSegment();
      this.syncVisiblePlaybackUI();
    }
  }

  applyMasterFilter(blockId, { autoplay = false } = {}) {
    if (!blockId) return;

    this.pauseAll();

    this.allCards.forEach((card) => {
      const isMatch = card.dataset.blockId === blockId;
      const video = card.querySelector('.bb-forge-masters__behind-chair-video');

      card.hidden = !isMatch;
      if (!isMatch) {
        card.classList.remove('is-active', 'is-video-paused');
        if (video) {
          this.detachVideoListeners(video);
          video.pause();
          video.preload = 'none';
          video.classList.remove('is-playing');
        }
      } else if (video) {
        video.preload = 'metadata';
      }
    });

    this.cards = this.allCards.filter((card) => !card.hidden);
    this.playlist = this.buildPlaylist();
    this.rebuildProgressSegments();

    if (this.carouselRoot) {
      this.carouselRoot.hidden = this.cards.length === 0;
    }

    this.root.classList.toggle('bb-forge-masters__behind-chair--single-slide', this.cards.length === 1);

    if (this.scroller) {
      this.scroller.scrollLeft = 0;
    }

    if (this.playlist.length === 0) {
      if (this.cards.length > 0) {
        this.finalizeActiveSlide(this.cards[0], false);
      }
      return;
    }

    this.currentIndex = 0;
    this.finalizeActiveSlide(this.playlist[0].card, false);

    if (autoplay) {
      this.playCurrent();
    } else {
      this.updateActiveSegment();
      this.syncVisiblePlaybackUI();
    }
  }

  rebuildProgressSegments() {
    if (!this.progressTrack || !this.progressRoot) return;

    this.progressTrack.innerHTML = '';
    this.progressSegments = [];

    const count = this.playlist.length;
    if (count <= 1) {
      this.progressRoot.hidden = true;
      return;
    }

    this.progressRoot.hidden = false;

    for (let index = 0; index < count; index += 1) {
      const segment = document.createElement('button');
      segment.type = 'button';
      segment.className = `bb-forge-masters__behind-chair-progress-segment${index === 0 ? ' is-active' : ''}`;
      segment.dataset.behindChairProgressSegment = '';
      segment.dataset.segmentIndex = String(index);
      segment.setAttribute('role', 'tab');
      segment.setAttribute('aria-label', `Video ${index + 1} of ${count}`);
      segment.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      segment.addEventListener('click', () => {
        if (index !== this.currentIndex && this.playingVideo) {
          this.playbackToken += 1;
          this.detachVideoListeners(this.playingVideo);
          this.playingVideo.pause();
          this.playingVideo.classList.remove('is-playing');
          this.playingVideo = null;
          this.syncVisiblePlaybackUI();
        }

        this.currentIndex = index;
        this.playCurrent();
      });
      this.progressTrack.appendChild(segment);
      this.progressSegments.push(segment);
    }
  }

  buildPlaylist() {
    return this.cards
      .map((card) => ({
        card,
        video: card.querySelector('.bb-forge-masters__behind-chair-video'),
      }))
      .filter((item) => item.video);
  }

  getCurrentItem() {
    return this.playlist[this.currentIndex];
  }

  detachVideoListeners(video) {
    if (!video) return;
    video.removeEventListener('ended', this.onEnded);
  }

  pauseAll() {
    this.playbackToken += 1;
    this.playingVideo = null;

    this.allCards.forEach((card) => {
      const video = card.querySelector('.bb-forge-masters__behind-chair-video');
      if (!video) return;
      this.detachVideoListeners(video);
      video.pause();
      video.classList.remove('is-playing');
    });
  }

  stopPlaylistVideos(exceptVideo = null) {
    this.playlist.forEach(({ video }) => {
      if (!video || video === exceptVideo) return;
      this.detachVideoListeners(video);
      video.pause();
      video.classList.remove('is-playing');
    });

    if (this.playingVideo && this.playingVideo !== exceptVideo) {
      this.detachVideoListeners(this.playingVideo);
      this.playingVideo.pause();
      this.playingVideo.classList.remove('is-playing');
      this.playingVideo = null;
    }
  }

  syncPlaybackUI(card) {
    if (!card) return;

    const video = card.querySelector('.bb-forge-masters__behind-chair-video');
    if (!video) return;

    card.classList.toggle(
      'is-video-paused',
      card.classList.contains('is-active') && (video.paused || video.ended)
    );
  }

  syncVisiblePlaybackUI() {
    this.cards.forEach((card) => this.syncPlaybackUI(card));
  }

  syncAllPlaybackUI() {
    this.allCards.forEach((card) => this.syncPlaybackUI(card));
  }

  async playCurrent() {
    const item = this.getCurrentItem();
    if (!item) return;

    const token = ++this.playbackToken;
    const { video, card } = item;

    this.stopPlaylistVideos(video);
    this.finalizeActiveSlide(card);

    video.muted = this.isMuted;
    video.currentTime = 0;
    this.detachVideoListeners(video);
    video.addEventListener('ended', this.onEnded);

    try {
      await video.play();
      if (token !== this.playbackToken) {
        video.pause();
        return;
      }

      this.playingVideo = video;
      video.classList.add('is-playing');
    } catch (_error) {
      if (token !== this.playbackToken) return;
      this.playingVideo = null;
      video.classList.remove('is-playing');
    }

    if (token !== this.playbackToken) return;

    this.finalizeActiveSlide(card);
    this.syncVisiblePlaybackUI();
    this.updateActiveSegment();
  }

  pauseCurrent() {
    const item = this.getCurrentItem();
    if (!item) return;

    const { video } = item;
    this.playbackToken += 1;
    this.detachVideoListeners(video);
    video.pause();
    this.playingVideo = null;
    video.classList.remove('is-playing');
    this.syncVisiblePlaybackUI();
  }

  async resumeCurrent() {
    const item = this.getCurrentItem();
    if (!item) return;

    const token = ++this.playbackToken;
    const { video } = item;

    this.stopPlaylistVideos(video);
    video.muted = this.isMuted;
    this.detachVideoListeners(video);
    video.addEventListener('ended', this.onEnded);

    try {
      await video.play();
      if (token !== this.playbackToken) {
        video.pause();
        return;
      }

      this.playingVideo = video;
      video.classList.add('is-playing');
    } catch (_error) {
      if (token !== this.playbackToken) return;
      this.playingVideo = null;
      video.classList.remove('is-playing');
    }

    if (token !== this.playbackToken) return;

    this.syncVisiblePlaybackUI();
  }

  handleEnded() {
    if (this.currentIndex < this.playlist.length - 1) {
      this.currentIndex += 1;
      this.playCurrent();
      return;
    }

    this.currentIndex = 0;
    this.playCurrent();
  }

  updateActiveSegment() {
    this.progressSegments.forEach((segment, index) => {
      const isActive = index === this.currentIndex;
      segment.classList.toggle('is-active', isActive);
      segment.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }

  setActiveCard(card) {
    this.allCards.forEach((entry) => {
      entry.classList.toggle('is-active', entry === card);
    });
    this.syncVisiblePlaybackUI();
  }

  finalizeActiveSlide(card, smooth = false) {
    if (!card) return;

    this.setActiveCard(card);
    this.root.classList.add('bb-behind-chair--no-card-transition');
    void card.offsetWidth;
    this.scrollCardIntoView(card, smooth);

    requestAnimationFrame(() => {
      if (!this.getCurrentItem() || this.getCurrentItem().card !== card) {
        this.root.classList.remove('bb-behind-chair--no-card-transition');
        return;
      }

      this.setActiveCard(card);
      this.scrollCardIntoView(card, false);

      requestAnimationFrame(() => {
        this.root.classList.remove('bb-behind-chair--no-card-transition');
      });
    });
  }

  scrollCardIntoView(card, smooth = true) {
    if (!this.scroller || !card || card.hidden) return;

    const track = card.parentElement;
    if (!track) return;

    const previousSnap = this.scroller.style.scrollSnapType;
    this.scroller.style.scrollSnapType = 'none';

    const centerCard = () => {
      const scrollerWidth = this.scroller.clientWidth;
      const cardWidth = card.offsetWidth;
      const maxScrollLeft = Math.max(0, this.scroller.scrollWidth - scrollerWidth);
      const targetScrollLeft = card.offsetLeft - (scrollerWidth - cardWidth) / 2;
      const clampedScrollLeft = Math.min(Math.max(0, targetScrollLeft), maxScrollLeft);

      if (smooth) {
        this.scroller.scrollTo({
          left: clampedScrollLeft,
          behavior: 'smooth',
        });
        return;
      }

      this.scroller.scrollLeft = clampedScrollLeft;
    };

    centerCard();

    requestAnimationFrame(() => {
      centerCard();
      this.scroller.style.scrollSnapType = previousSnap || '';
    });
  }

  toggleMute() {
    this.isMuted = !this.isMuted;
    this.root.classList.toggle('is-unmuted', !this.isMuted);

    this.playlist.forEach(({ video }) => {
      video.muted = this.isMuted;
    });

    const label = this.isMuted ? 'Unmute video' : 'Mute video';
    this.root.querySelectorAll('[data-behind-chair-mute]').forEach((control) => {
      control.setAttribute('aria-label', label);
    });
  }
}

const initBBBehindTheChair = (container = document) => {
  container.querySelectorAll('[data-bb-behind-chair]').forEach((root) => {
    if (root.dataset.bbBehindChairReady === 'true') return;
    root.dataset.bbBehindChairReady = 'true';
    new BBBehindTheChair(root);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initBBBehindTheChair());
} else {
  initBBBehindTheChair();
}

document.addEventListener('shopify:section:load', (event) => {
  initBBBehindTheChair(event.target);
});
